import moment from 'moment-timezone';

export type TimeRange = {
  id: string;
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  startTime: string; // Format: "HH:mm"
  endTime: string; // Format: "HH:mm"
};

export type Schedule = {
  id: string;
  startDate: string; // ISO datetime string
  endDate?: string | null; // ISO datetime string
  timeRanges?: TimeRange[] | null;
};

export function isScheduleActiveNow(schedule: Schedule | null | undefined, timezone: string): boolean {
  if (!schedule) {
    return true;
  }

  const now = moment.tz(timezone);

  // Check date range boundaries
  if (schedule.startDate) {
    const startMoment = moment.utc(schedule.startDate).tz(timezone);
    if (now.isBefore(startMoment)) {
      return false;
    }
  }

  if (schedule.endDate) {
    const endMoment = moment.utc(schedule.endDate).tz(timezone).endOf('day');
    if (now.isAfter(endMoment)) {
      return false;
    }
  }

  // If no time ranges, schedule is active all day within date range
  if (!schedule.timeRanges || schedule.timeRanges.length === 0) {
    return true;
  }

  // Check if current day/time matches any time range
  const currentDay = now.day(); // 0 = Sunday, 6 = Saturday
  const currentTime = now.format('HH:mm');

  return schedule.timeRanges.some((timeRange) => {
    const { dayOfWeek, startTime, endTime } = timeRange;

    if (dayOfWeek !== currentDay) {
      return false;
    }

    // Handle time ranges that don't span midnight
    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    }

    // Handle time ranges that span midnight (e.g., 22:00 to 06:00)
    return currentTime >= startTime || currentTime <= endTime;
  });
}
